import React, {PropsWithChildren, useMemo} from 'react';
import {DragDropContext, Droppable} from '@hello-pangea/dnd';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {MAX_NUMBER_OF_SEQUENCE_ITEMS} from '../../../../../constants';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementSequenceQuestion} from '../../../../../incase-domain/elements/question/questionSequence';
import {removeArrayElement, shuffleElementsOrder, swapArrayElement, swapArrayIndices} from '../../../../../utils/utils';
import {generateErrorMessages, getErrorClasses} from '../../../../../utils/validation';
import SectionTitle from '../../../../elements/common/SectionTitle';
import SequenceQuestionAnswer from './SequenceQuestionAnswer';

interface AnswersProps {
    highlightedElement: IElementSequenceQuestion;
    errors: IValidationErrorObject | null;
    highlightedElementChangeHandler: {
        single: (field: keyof IElementSequenceQuestion | string, value: unknown) => void;
        multiple: (fields: (keyof IElementSequenceQuestion)[], values: unknown[]) => void;
    };
}

function SequenceQuestionAnswers(props: PropsWithChildren<AnswersProps>): JSX.Element {
    const {highlightedElement, errors: validationErrors, highlightedElementChangeHandler, children} = props;
    const {t} = useTranslation();

    const indexMap = useMemo(() => {
        const indexMap: Record<number, number> = {};
        const used: number[] = [];
        highlightedElement.correctSequence.forEach((item, index) => {
            indexMap[index] = highlightedElement.sequence.findIndex((answer, index) => {
                const isMatch = answer.text === item && !used.includes(index);
                if (isMatch) used.push(index);
                return isMatch;
            });
        });

        return indexMap;
    }, [highlightedElement.correctSequence, highlightedElement.sequence]);

    function addSequenceItem(): void {
        if (highlightedElement.sequence.length >= MAX_NUMBER_OF_SEQUENCE_ITEMS) {
            toast.error(t('designSidepanel.tooManySequenceItems'));
            return;
        }

        const newSequenceName = `${t('presEditPage.editSidebar.elements.initialStates.sequenceItem')} ${
            highlightedElement.sequence.length
        }`;

        let newSequence = [...highlightedElement.sequence, {text: newSequenceName, comment: ''}];
        if (highlightedElement.automaticShuffle) {
            newSequence = shuffleElementsOrder(newSequence);
        }

        const newCorrectSequence = [...highlightedElement.correctSequence, newSequenceName];
        highlightedElementChangeHandler.multiple(['sequence', 'correctSequence'], [newSequence, newCorrectSequence]);
    }

    function removeSequenceItem(index: number): void {
        const correctSequenceIndex = highlightedElement.correctSequence.findIndex(
            (correctSequenceItem) => correctSequenceItem === highlightedElement.sequence[index].text
        );
        const newSequence = removeArrayElement(highlightedElement.sequence, index);
        const newCorrectSequence = removeArrayElement(highlightedElement.correctSequence, correctSequenceIndex);

        highlightedElementChangeHandler.multiple(['sequence', 'correctSequence'], [newSequence, newCorrectSequence]);
    }

    function sequenceItemChangeHandler(index: number, correctSequenceIndex: number, value: string): void {
        const changedSequenceItem = {...highlightedElement.sequence[index]};
        changedSequenceItem.text = value;

        const newSequence = swapArrayElement(highlightedElement.sequence, index, changedSequenceItem);
        const newCorrectSequence = [...highlightedElement.correctSequence];
        newCorrectSequence[correctSequenceIndex] = value;

        highlightedElementChangeHandler.multiple(['sequence', 'correctSequence'], [newSequence, newCorrectSequence]);
    }

    function swapCorrectSequenceItemOrderHandler(newIndex: number, oldIndex: number): void {
        const newCorrectSequence = swapArrayIndices(highlightedElement.correctSequence, newIndex, oldIndex);
        highlightedElementChangeHandler.single('correctSequence', newCorrectSequence);
    }

    return (
        <div className="l-group--form c-newAnswer">
            <SectionTitle
                label={t('presEditPage.editSidebar.elements.sequenceQuestion.items')}
                leading={
                    <>
                        <Button
                            className="c-sequenceQuestionElementOrder__shuffleButton"
                            size="2xs"
                            variant="ghost"
                            color="primary"
                            onClick={addSequenceItem}
                            leading="ph:plus-bold"
                        >
                            {t('designSidepanel.new')}
                        </Button>
                    </>
                }
            />
            <div className={`${getErrorClasses(validationErrors, `answers`)} ${getErrorClasses(validationErrors, `sequence`)}`}>
                {generateErrorMessages(validationErrors, `answers`)}
                {generateErrorMessages(validationErrors, `sequence`)}

                <DragDropContext
                    onDragEnd={(e) => {
                        if (typeof e.destination?.index !== 'undefined')
                            swapCorrectSequenceItemOrderHandler(e.destination.index, e.source.index);
                    }}
                >
                    <Droppable droppableId={`correct-sequence-${highlightedElement.id}`}>
                        {(provided) => {
                            return (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {highlightedElement.correctSequence.map((answerText, correctSequenceIndex) => {
                                        return (
                                            <div key={`highlighted-element-sequence-answer-${correctSequenceIndex}`}>
                                                <SequenceQuestionAnswer
                                                    answer={answerText}
                                                    validationErrors={validationErrors}
                                                    answerChangeHandler={(index, value) => {
                                                        sequenceItemChangeHandler(index, correctSequenceIndex, value);
                                                    }}
                                                    highlightedElement={highlightedElement}
                                                    highlightedElementChangeHandler={highlightedElementChangeHandler}
                                                    index={indexMap[correctSequenceIndex]}
                                                    correctSequenceIndex={correctSequenceIndex}
                                                    removeAnswer={removeSequenceItem}
                                                />
                                            </div>
                                        );
                                    })}

                                    {provided.placeholder}
                                </div>
                            );
                        }}
                    </Droppable>
                </DragDropContext>
            </div>

            {children}
        </div>
    );
}

export default SequenceQuestionAnswers;
